import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { getSupabase } from '../lib/supabase';
import { authErrorMessage } from '../lib/authErrors';
import {
  clearPendingPasswordSetup,
  usePendingPasswordSetup,
  type PendingAuthFlow,
} from '../lib/authFlow';
import { AppLogo, Button, Card, FormField, Input } from '../ui';

// Password setup for an invited user (and for a recovery link). Reached only while
// `usePendingPasswordSetup()` is set — the guard sends every other route here until the
// password is chosen, and this screen sends the user back out once it is.
//
// Minimum length is 8 and nothing more: any stricter client rule would be a second, silently
// diverging policy next to the real one, which lives in the Supabase project settings. Eight
// is at or below every Supabase default, so this can only ever agree with the server.
const MIN_PASSWORD_LENGTH = 8;

const COPY: Record<PendingAuthFlow, { title: string; lead: string; submit: string }> = {
  invite: {
    title: 'პაროლის შექმნა',
    lead: 'კეთილი იყოს თქვენი მობრძანება. სისტემაში შესასვლელად შექმენით პაროლი.',
    submit: 'პაროლის შექმნა',
  },
  recovery: {
    title: 'ახალი პაროლი',
    lead: 'შეიყვანეთ ახალი პაროლი ანგარიშზე წვდომის აღსადგენად.',
    submit: 'პაროლის შენახვა',
  },
};

export function SetPasswordPage(): JSX.Element {
  const flow = usePendingPasswordSetup();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Nothing pending — either the password was just saved (`done`) or the screen was opened
  // directly. Both cases belong in the app, not here.
  if (flow === null) {
    return <Navigate to="/" replace />;
  }
  const copy = COPY[flow];

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting || done) return;

    setError(null);
    setFieldError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFieldError(`პაროლი უნდა შეიცავდეს მინიმუმ ${MIN_PASSWORD_LENGTH} სიმბოლოს.`);
      return;
    }
    if (password !== confirmation) {
      setFieldError('პაროლები არ ემთხვევა.');
      return;
    }

    setSubmitting(true);
    // try/catch, not just the `error` channel: a transport failure (or any SDK surprise)
    // REJECTS rather than resolving, and an unhandled rejection here would leave the button
    // spinning with nothing on screen — the user stuck, with no way to tell why.
    let updateError: unknown = null;
    try {
      updateError = (await getSupabase().auth.updateUser({ password })).error;
    } catch (thrown) {
      updateError = thrown;
    }
    if (updateError !== null && updateError !== undefined) {
      setError(authErrorMessage(updateError, 'პაროლის შენახვა ვერ მოხერხდა. სცადეთ ხელახლა.'));
      setSubmitting(false);
      return;
    }
    // Order matters: mark done first so the flag change cannot render this screen mid-transition.
    setDone(true);
    clearPendingPasswordSetup();
  };

  return (
    <div className="flex min-h-full w-full flex-col items-center justify-center gap-6 p-6">
      <AppLogo />
      <Card padded={false} elevated className="w-full max-w-sm p-6">
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4" noValidate>
          <div className="space-y-1">
            <h1 className="text-lg font-semibold text-fg">{copy.title}</h1>
            <p className="text-sm text-fg-muted">{copy.lead}</p>
          </div>

          <FormField
            label="პაროლი"
            required
            hint={`მინიმუმ ${MIN_PASSWORD_LENGTH} სიმბოლო`}
            {...(fieldError !== null ? { error: fieldError } : {})}
          >
            <Input
              type="password"
              autoComplete="new-password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>

          <FormField label="გაიმეორეთ პაროლი" required>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />
          </FormField>

          {error !== null && (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          )}

          <Button type="submit" className="w-full" loading={submitting}>
            {copy.submit}
          </Button>
        </form>
      </Card>
    </div>
  );
}
